import React, { useMemo, useState, useEffect } from "react";
import { Col, Form, Row, Button } from "react-bootstrap";
import Select from "react-select";
import DatePicker from "react-datepicker";
import Moment from "moment";
import { get } from "aws-amplify/api";
import SLMSearchPageShell from "../SLMSearchPageShell";
import SLMLeaseList from "./SLMLeaseList";
import "react-datepicker/dist/react-datepicker.css";
import "./SLMLeaseSearch.css";

function SLMLeaseSearch({ isAuthenticated }) {
  const [isLoading, setIsLoading] = useState(true);
  const [leases, setLeases] = useState([]);
  const [filter, setFilter] = useState({});
  const [searchCount, setSearchCount] = useState(0);
  const [stateOptions, setStateOptions] = useState([]);
  const [broadSpaceUseOptions, setBroadSpaceUseOptions] = useState([]);
  const [spaceUseOptions, setSpaceUseOptions] = useState([]);
  const [leaseMethodOptions, setLeaseMethodOptions] = useState([]);

  const [selectedState, setSelectedState] = useState(null);
  const [selectedBroadSpaceUseCategories, setSelectedBroadSpaceUseCategories] = useState(null);
  const [selectedSpaceUseCategories, setSelectedSpaceUseCategories] = useState(null);
  const [selectedLeaseMethodCategories, setSelectedLeaseMethodCategories] = useState(null);

  const [createdby, setCreatedby] = useState("");
  const [editedby, setEditedby] = useState("");
  const [printdatefrom, setPrintdatefrom] = useState(null);
  const [printdateto, setPrintdateto] = useState(null);
  const [statdatefrom, setStatdatefrom] = useState(null);
  const [statdateto, setStatdateto] = useState(null);
  const [rentpsqmfrom, setRentpsqmfrom] = useState("");
  const [rentpsqmto, setRentpsqmto] = useState("");
  const [rentpannumfrom, setRentpannumfrom] = useState("");
  const [rentpannumto, setRentpannumto] = useState("");
  const [broadspace, setBroadspace] = useState("");
  const [spaceuse, setSpaceuse] = useState("");
  const [leasemethod, setLeasemethod] = useState("");
  const [buildingname, setBuildingname] = useState("");
  const [streetname, setStreetname] = useState("");
  const [streetext, setStreetext] = useState("");
  const [suburb, setSuburb] = useState("");
  const [postcode, setPostcode] = useState("");
  const [state, setState] = useState("");
  const [agent, setAgent] = useState("");
  const [agency, setAgency] = useState("");
  const [lessee, setLessee] = useState("");
  const [lessor, setLessor] = useState("");
  const [leasetype, setLeasetype] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    document.title = "SLM Lease Search";

    (async () => {
      try {
        const [statesResponse, slmLookupsResponse] = await Promise.all([
          get({ apiName: "colin", path: "/slmstates" }).response,
          get({ apiName: "colin", path: "/slmlookups" }).response,
        ]);

        const [states, slmLookups] = await Promise.all([statesResponse.body.json(), slmLookupsResponse.body.json()]);

        const lookupGroups = Array.isArray(slmLookups) ? slmLookups : [];
        const broadSpaceUseLookup = lookupGroups.find((item) => item?.look_up === "broad_space_use");
        const spaceUseLookup = lookupGroups.find((item) => item?.look_up === "space_use");
        const leaseMethodLookup = lookupGroups.find((item) => item?.look_up === "lease_type_category");

        setStateOptions(states || []);
        setBroadSpaceUseOptions(Array.isArray(broadSpaceUseLookup?.list) ? broadSpaceUseLookup.list : []);
        setSpaceUseOptions(Array.isArray(spaceUseLookup?.list) ? spaceUseLookup.list : []);
        setLeaseMethodOptions(Array.isArray(leaseMethodLookup?.list) ? leaseMethodLookup.list : []);
      } catch (error) {
        alert(error + ". Please refresh the page (F5) to try again.");
      } finally {
        setIsLoading(false);
      }
    })();
  }, [isAuthenticated]);

  const numericValue = (value) => String(value ?? "").replace(/[^0-9.]/g, "");

  const hasValidDate = (value) => value instanceof Date && !Number.isNaN(value.getTime());

  const buildFilter = () => ({
    createdby,
    editedby,
    printdatefrom: printdatefrom ? Moment(printdatefrom).local().format() : "",
    printdateto: printdateto ? Moment(printdateto).local().format() : "",
    statdatefrom: statdatefrom ? Moment(statdatefrom).local().format() : "",
    statdateto: statdateto ? Moment(statdateto).local().format() : "",
    rentpsqmfrom,
    rentpsqmto,
    rentpannumfrom,
    rentpannumto,
    broadspace,
    spaceuse,
    leasemethod,
    buildingname,
    streetname,
    streetext,
    suburb,
    postcode,
    state,
    agent,
    agency,
    lessee,
    lessor,
    leasetype: leasetype ?? "",
  });

  const isExportTimeoutError = (error) => {
    const statusCode =
      error?.statusCode ||
      error?.response?.status ||
      error?.$metadata?.httpStatusCode ||
      error?.cause?.statusCode;

    if (statusCode === 504) {
      return true;
    }

    const details = [
      error?.name,
      error?.message,
      error?.code,
      error?.cause?.message,
      typeof error === "string" ? error : "",
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return (
      details.includes("timeout") ||
      details.includes("timed out") ||
      details.includes("504") ||
      details.includes("networkerror") ||
      details.includes("network error")
    );
  };

  const getLeases = async (activeFilter) => {
    const response = await get({
      apiName: "colin",
      path: "/slmleases",
      options: { queryParams: activeFilter },
    }).response;

    const results = await response.body.json();
    return results;
  };

  const exportPimsRecords = async () => {
    if (!hasValidDate(printdatefrom) || !hasValidDate(printdateto)) {
      alert("You must enter print date from and to before running an export");
      return;
    }

    if (!state) {
      alert("You must select a state before running an export");
      return;
    }

    setIsLoading(true);

    try {
      const activeFilter = buildFilter();
      const response = await get({
        apiName: "colin",
        path: "/slmleasesexport",
        options: { queryParams: activeFilter },
      }).response;

      const payload = await response.body.json();

      let pimContent = "";
      if (typeof payload === "string") {
        pimContent = payload;
      } else {
        const exportRows = Array.isArray(payload)
          ? Array.isArray(payload[0]?.results)
            ? payload[0].results
            : payload
          : Array.isArray(payload?.results)
            ? payload.results
            : [];

        pimContent = exportRows
          .map((row) =>
            Object.values(row || {})
              .map((value) => {
                if (value === null || value === undefined) {
                  return "";
                }
                if (typeof value === "number") {
                  return value;
                }
                const cleaned = String(value).replace(/\r/g, "").replace(/\n/g, "").replace(/"/g, '""');
                return `"${cleaned}"`;
              })
              .join(",")
          )
          .join("\r\n");
      }

      if (!pimContent) {
        alert("No records found for export.");
        return;
      }

      const exportDate = activeFilter.printdateto ? new Date(activeFilter.printdateto) : new Date();
      const validExportDate = Number.isNaN(exportDate.getTime()) ? new Date() : exportDate;
      const yy = String(validExportDate.getFullYear()).slice(2, 4);
      const mm = String(validExportDate.getMonth() + 1).padStart(2, "0");
      const dd = String(validExportDate.getDate()).padStart(2, "0");
      const stateSuffixByCode = {
        ACT: "a",
        NT: "d",
        NSW: "n",
        QLD: "q",
        SA: "s",
        TAS: "t",
        VIC: "v",
        WA: "w",
      };
      const stateSuffix = stateSuffixByCode[activeFilter.state] || "";
      const fileName = `l${yy}${mm}${dd}${stateSuffix}.pim`;

      const blob = new Blob([pimContent], { type: "text/plain;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      const exportErrorMessage = "The PIM export is taking longer than expected due to a large amount of data requested. Kindly refine your search filters and try again.";
      if (isExportTimeoutError(error)) {
        alert(exportErrorMessage);
      } else {
        alert(exportErrorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);

    const activeFilter = buildFilter();

    try {
      const nextLeases = await getLeases(activeFilter);
      setLeases(nextLeases || []);
      setFilter(activeFilter);
      setSearchCount((count) => count + 1);
    } catch (error) {
      alert(error + ". Please refresh the page (F5) to try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const clearFilters = () => {
    setCreatedby("");
    setEditedby("");
    setPrintdatefrom(null);
    setPrintdateto(null);
    setStatdatefrom(null);
    setStatdateto(null);
    setRentpsqmfrom("");
    setRentpsqmto("");
    setRentpannumfrom("");
    setRentpannumto("");
    setBroadspace("");
    setSpaceuse("");
    setLeasemethod("");
    setBuildingname("");
    setStreetname("");
    setStreetext("");
    setSuburb("");
    setPostcode("");
    setState("");
    setAgent("");
    setAgency("");
    setLessee("");
    setLessor("");
    setLeasetype(null);
    setSelectedState(null);
    setSelectedBroadSpaceUseCategories(null);
    setSelectedSpaceUseCategories(null);
    setSelectedLeaseMethodCategories(null);
  };

  const filteredSpaceUseOptions = useMemo(
    () =>
      (spaceUseOptions || []).filter((option) => {
        if (!broadspace) {
          return true;
        }

        return String(option?.supplementary_details?.broad_space_use_id ?? "") === String(broadspace);
      }),
    [spaceUseOptions, broadspace]
  );

  const topActions = (
    <>
      <Row className="mb-2 g-2">
        <Col xs={12}>
          <div className="slm-lease-radio-group">
            <div className="slm-lease-radio-option">
              <input
                id="lease_type_leased"
                name="lease_type"
                type="radio"
                className="slm-lease-radio-input"
                checked={leasetype === "Leased"}
                onChange={() => setLeasetype("Leased")}
              />
              <label htmlFor="lease_type_leased" className="slm-lease-radio-label">Leased</label>
            </div>

            <div className="slm-lease-radio-option">
              <input
                id="lease_type_for_lease"
                name="lease_type"
                type="radio"
                className="slm-lease-radio-input"
                checked={leasetype === "For Lease"}
                onChange={() => setLeasetype("For Lease")}
              />
              <label htmlFor="lease_type_for_lease" className="slm-lease-radio-label">For Lease</label>
            </div>

            <div className="slm-lease-radio-option">
              <input
                id="lease_type_both"
                name="lease_type"
                type="radio"
                className="slm-lease-radio-input"
                checked={leasetype === "Both"}
                onChange={() => setLeasetype("Both")}
              />
              <label htmlFor="lease_type_both" className="slm-lease-radio-label">Both</label>
            </div>
          </div>
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col xs={12} sm={6}>
          <Form.Group controlId="createdby">
            <Form.Control
              value={createdby}
              onChange={(event) => setCreatedby(event.target.value)}
              placeholder="Created By starting"
            />
          </Form.Group>
        </Col>
        <Col xs={12} sm={6}>
          <Form.Group controlId="editedby">
            <Form.Control
              value={editedby}
              onChange={(event) => setEditedby(event.target.value)}
              placeholder="Last Edited By starting"
            />
          </Form.Group>
        </Col>
      </Row>
    </>
  );

  const filters = (
    <>
      <Row className="mb-2 g-2">
        <Col xs={12} lg={4} className="d-flex align-items-center">Print Date</Col>
        <Col xs={12} sm={6} lg={4}>
          <DatePicker
            selected={printdatefrom}
            onChange={(date) => setPrintdatefrom(date || null)}
            dateFormat="dd/MM/yyyy"
            className="form-control"
            isClearable
            placeholderText="From"
          />
        </Col>
        <Col xs={12} sm={6} lg={4}>
          <DatePicker
            selected={printdateto}
            onChange={(date) => setPrintdateto(date || null)}
            dateFormat="dd/MM/yyyy"
            className="form-control"
            isClearable
            placeholderText="To"
          />
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col xs={12} lg={4} className="d-flex align-items-center">Stat Date</Col>
        <Col xs={12} sm={6} lg={4}>
          <DatePicker
            selected={statdatefrom}
            onChange={(date) => setStatdatefrom(date || null)}
            dateFormat="dd/MM/yyyy"
            className="form-control"
            isClearable
            placeholderText="From"
          />
        </Col>
        <Col xs={12} sm={6} lg={4}>
          <DatePicker
            selected={statdateto}
            onChange={(date) => setStatdateto(date || null)}
            dateFormat="dd/MM/yyyy"
            className="form-control"
            isClearable
            placeholderText="To"
          />
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col xs={12} lg={4} className="d-flex align-items-center">Rent per SQM</Col>
        <Col xs={12} sm={6} lg={4}>
          <Form.Group controlId="rentpsqmfrom">
            <Form.Control value={rentpsqmfrom} onChange={(event) => setRentpsqmfrom(numericValue(event.target.value))} placeholder="From" />
          </Form.Group>
        </Col>
        <Col xs={12} sm={6} lg={4}>
          <Form.Group controlId="rentpsqmto">
            <Form.Control value={rentpsqmto} onChange={(event) => setRentpsqmto(numericValue(event.target.value))} placeholder="To" />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col xs={12} lg={4} className="d-flex align-items-center">Rent per Annum</Col>
        <Col xs={12} sm={6} lg={4}>
          <Form.Group controlId="rentpannumfrom">
            <Form.Control value={rentpannumfrom} onChange={(event) => setRentpannumfrom(numericValue(event.target.value))} placeholder="From" />
          </Form.Group>
        </Col>
        <Col xs={12} sm={6} lg={4}>
          <Form.Group controlId="rentpannumto">
            <Form.Control value={rentpannumto} onChange={(event) => setRentpannumto(numericValue(event.target.value))} placeholder="To" />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col>
          <Select
            value={selectedBroadSpaceUseCategories}
            onChange={(option) => {
              setSelectedBroadSpaceUseCategories(option || null);
              setBroadspace(option?.value ?? "");
              setSelectedSpaceUseCategories(null);
              setSpaceuse("");
            }}
            options={broadSpaceUseOptions || []}
            placeholder="Broad Use"
            isClearable
          />
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col>
          <Select
            value={selectedSpaceUseCategories}
            onChange={(option) => {
              setSelectedSpaceUseCategories(option || null);
              setSpaceuse(option?.value ?? "");
              const broadSpaceValue = option?.supplementary_details?.broad_space_use_id;
              if (broadSpaceValue) {
                setBroadspace(broadSpaceValue);
                const matchingBroadOption = (broadSpaceUseOptions || []).find((broadOption) => String(broadOption?.value) === String(broadSpaceValue));
                setSelectedBroadSpaceUseCategories({
                  value: broadSpaceValue,
                  label: matchingBroadOption?.label ?? option?.supplementary_details?.broad_space_use_code ?? String(broadSpaceValue),
                });
              }
            }}
            options={filteredSpaceUseOptions}
            placeholder="Space Use"
            isClearable
          />
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col>
          <Select
            value={selectedLeaseMethodCategories}
            onChange={(option) => {
              setSelectedLeaseMethodCategories(option || null);
              setLeasemethod(option?.value ?? "");
            }}
            options={leaseMethodOptions || []}
            placeholder="Lease Method"
            isClearable
          />
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col>
          <Form.Group controlId="buildingname">
            <Form.Control value={buildingname} onChange={(event) => setBuildingname(event.target.value)} placeholder="Building Name containing" />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col xs={12} sm={8}>
          <Form.Group controlId="streetname">
            <Form.Control value={streetname} onChange={(event) => setStreetname(event.target.value)} placeholder="Street Name starting" />
          </Form.Group>
        </Col>
        <Col xs={12} sm={4}>
          <Form.Group controlId="streetext">
            <Form.Control value={streetext} onChange={(event) => setStreetext(event.target.value)} placeholder="Street Ext" />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col>
          <Form.Group controlId="suburb">
            <Form.Control value={suburb} onChange={(event) => setSuburb(event.target.value)} placeholder="Suburb starting" />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col xs={12} sm={8}>
          <Select
            value={selectedState}
            onChange={(option) => {
              setSelectedState(option || null);
              setState(option?.value ?? "");
            }}
            options={stateOptions}
            placeholder="Select a state"
            isClearable
          />
        </Col>
        <Col xs={12} sm={4}>
          <Form.Group controlId="postcode">
            <Form.Control value={postcode} onChange={(event) => setPostcode(event.target.value)} placeholder="Postcode" />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col>
          <Form.Group controlId="agent">
            <Form.Control value={agent} onChange={(event) => setAgent(event.target.value)} placeholder="Agent containing" />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col>
          <Form.Group controlId="agency">
            <Form.Control value={agency} onChange={(event) => setAgency(event.target.value)} placeholder="Agency containing" />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col>
          <Form.Group controlId="lessee">
            <Form.Control value={lessee} onChange={(event) => setLessee(event.target.value)} placeholder="Lessee containing" />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col>
          <Form.Group controlId="lessor">
            <Form.Control value={lessor} onChange={(event) => setLessor(event.target.value)} placeholder="Lessor containing" />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col xs="auto">
          <Button id="export_pims_button_leases" type="button" size="sm" onClick={exportPimsRecords}>
            Export PIMS
          </Button>
        </Col>
      </Row>
    </>
  );

  if (!isAuthenticated) {
    return null;
  }

  return (
    <SLMSearchPageShell
      className="SLMLeaseSearch"
      title="SLM Leases"
      isLoading={isLoading}
      onSubmit={handleSubmit}
      onClear={clearFilters}
      filterPanel={filters}
      filterDensity="tight"
      topActions={topActions}
      resultsPanel={
        <SLMLeaseList
          filter={filter}
          isAuthenticated={isAuthenticated}
          isLoading={isLoading}
          data={leases}
          search_count={searchCount}
        />
      }
    />
  );
}

export default SLMLeaseSearch;
